import { useGetUserContextQuery } from '../../infra/adapters/userContextApiSlice';
import { adaptUserContextResponseDtoToDomain } from '../../infra/mappers/userContext.mapper';
import { useEffect } from 'react';
import { setItem as setSessionItem, removeItem as removeSessionItem } from '@/core/services/storage/utils/sessionStorageUtil';
import { useAppDispatch } from '@/app/store/store';
import { setAbUid } from '@/shared/features/ab-testing/store/abTestingSlice';
import type { UserInfo } from '@/shared/features/analytics/utils/commonAttributes';

/**
 * User Context Feature - Presentation Hook
 * Uses RTK Query hook directly and exposes domain model to UI.
 */
export function useUserContext() {
  const dispatch = useAppDispatch();
  const { data, isLoading, error: queryError } = useGetUserContextQuery(undefined, {
    // Always refetch on mount so the browser session state is authoritative.
    refetchOnMountOrArgChange: true,
  });

  // Convert plain object from Redux to domain model
  const source = (data as Record<string, unknown> | undefined) ?? null;
  const userContext = source ? adaptUserContextResponseDtoToDomain(source) : null;

  // Keep AB bucketing key in sync with latest client user-context response.
  useEffect(() => {
    if (!userContext || typeof userContext.visitorId !== 'number' || !Number.isFinite(userContext.visitorId)) {
      dispatch(setAbUid(null));
      return;
    }
    dispatch(setAbUid(String(userContext.visitorId)));
  }, [dispatch, userContext]);

  // Keep analytics userinfo in sync for commonAttributes fallback.
  // This ensures "Logged In" (and a stable user id) is available even if
  // other parts of the app rely on sessionStorage.
  useEffect(() => {
    if (!userContext) return;

    try {
      const record = source as Record<string, unknown>;
      const customerIdRaw = userContext.isLoggedIn
        ? (record.customerId ?? record.userCode)
        : record.customerId;
      const visitorId = record.visitorId;
      const pincode = record.pincode;
      const softLoginType = record.softLoginType;

      const userCodeRaw = record.userCode;
      const userinfo: UserInfo = {
        isLoggedIn: userContext.isLoggedIn,
        ...(typeof userCodeRaw === 'string' && userCodeRaw.trim()
          ? { userCode: userCodeRaw.trim() }
          : {}),
        ...(customerIdRaw != null && String(customerIdRaw).trim() !== ''
          ? { customerId: String(customerIdRaw) }
          : {}),
        ...(visitorId != null ? { visitorId: String(visitorId) } : {}),
        ...(typeof pincode === 'string' ? { pincode } : {}),
        ...(typeof softLoginType === 'string' && softLoginType.trim()
          ? { softLoginType: softLoginType.trim() }
          : {}),
      };

      if (userContext.isLoggedIn) {
        setSessionItem('userinfo', userinfo);
      } else {
        removeSessionItem('userinfo');
        setSessionItem('userinfo', { ...userinfo, isLoggedIn: false });
      }
    } catch {
      // Storage may be unavailable (private mode / SSR); ignore.
    }
  }, [userContext, source]);

  return {
    userContext,
    customerId: (source?.customerId ?? source?.userCode ?? null) as string | number | null,
    userCode: (source?.userCode ?? null) as string | null,
    isLoading,
    error: queryError ? (queryError as { message?: string })?.message || 'Failed to fetch user context' : null,
  };
}

